import {state} from 'lit/decorators.js';
import {html, LitElement, nothing, PropertyValues, TemplateResult} from 'lit';
import {BdkList} from '@breuni-ui/bdk-list';
import {safeDefine} from '@breuni-ui/safe-define';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {Article, Messages, Product} from '../../../@types/wishlist.js';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {consume} from '@lit/context';
import {ProductIdentifier} from '../../../scripts/service/product-identifier.js';
import {
  trackRemoveFromWishlist,
  trackSnackbarUndoWishlistItemDelete
} from '../../../scripts/service/cube-tracking-layer.js';
import {wishlistService} from '../../../scripts/service/wishlist-service.js';
import {ADD_TO_WISHLIST_EVENT} from '../../../scripts/service/config.js';
import {pushToSnackbar} from '../../../scripts/service/snackbar-service.js';

safeDefine('bdk-ents-list', BdkList);

export interface ActionItem {
  label: string | TemplateResult;
  icon?: SVGElement;
  arrow: boolean;
  callback: () => Promise<void> | void;
  trackingKey: 'size_selection' | 'remove' | 'vsp' | 'wvb' | 'hva' | 'share' | 'mix&match' | 'productline' | 'rename'
    | 'add_to_collection' | 'capsule' | 'view_order';
}

const wait = async (ms: number) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

export abstract class ActionMenu extends LitElement {
  protected testId: string = 'ents-action-menu';

  @state()
  protected showLayer: boolean = false;

  @consume({context: i18nContext})
  protected messages!: Messages;

  protected handleLayerClosed() {
    this.updateStateOnClose();
    if (this.showLayer) {
      // this is needed because when base layer closes itself (e.g. click on 'x' or backdrop)
      // it is not automatically reflected on the 'showLayer' state
      this.showLayer = false;
    }
  }

  protected updateStateOnClose() {
    return;
  }

  protected closeLayer() {
    if (this.showLayer) {
      this.showLayer = false;
    }
  }

  protected renderBanners(): TemplateResult | symbol {
    return nothing;
  }

  protected render(): TemplateResult {
    return html`
      <ents-base-layer
        ?showLayer="${this.showLayer}"
        drawer-test-id="${this.testId}"
        @ents-layer-closed="${this.handleLayerClosed}"
      >
        ${this.renderInformation()}
        <div class="ents-action-menu">
          ${this.renderBanners()}
          <div class="ents-action-menu__items">
            ${this.getApplicableActionItems().map(actionItem => this.renderActionItem(actionItem))}
          </div>
        </div>
      </ents-base-layer>
    `;
  }

  shouldUpdate(changedProperties: PropertyValues): boolean {
    if (changedProperties.size === 0) {
      return false;
    }
    return super.shouldUpdate(changedProperties);
  }

  protected renderInformation(): TemplateResult | symbol {
    return nothing;
  }

  protected renderLabel(label: string | TemplateResult): TemplateResult {
    if (typeof label != 'string') {
      return label;
    }
    // Span to preserve spaces
    const unsafeLabel = unsafeHTML(`<span>${label}</span>`);
    return html`${unsafeLabel}`;
  }

  private renderActionItem(actionItem: ActionItem): TemplateResult {
    const icon = actionItem.icon;
    if (icon) {
      icon.setAttribute('slot', 'icon');
    }
    return html`
      <bdk-ents-list
        data-test-ents-action-menu-item
        role="list"
        ?with-arrow="${actionItem.arrow}"
        ?with-icon="${icon != undefined}"
        @bdk-list-click="${() => this.handleBdkListItemClick(actionItem)}"
      >
        ${icon ?? nothing}
        ${this.renderLabel(actionItem.label)}
      </bdk-ents-list>
    `;
  }

  private async handleBdkListItemClick(actionItem: ActionItem) {
    this.closeLayer();
    await this.handleActionItemClick(actionItem);
  }

  private async handleActionItemClick(actionItem: ActionItem) {
    await wait(320); // base layer slide out animation takes 300ms, so we wait an extra 20ms to avoid buggy UI
    actionItem.callback();
  }

  protected getApplicableActionItems(): ActionItem[] {
    return [];
  }

  // Remove from wishlist helpers

  private buildWishlistRemoveData(product: Product, selectedArticle: Article | null) {
    return {
      identifier: ProductIdentifier.createByProduct(product).id,
      syan: product.syan,
      colorId: product.colorId,
      articleId: selectedArticle?.id,
      added: product.added,
      purchased: product.purchaseDetails?.purchased,
      collectionReferences: product.collectionReferences,
      tracking: {
        placement: 'wishlist',
        context: 'wishlist_action_menu',
        added: product.added,
      },
    };
  }

  protected executeWishlistRemove(
    product: Product,
    selectedArticle: Article | null,
    isOnAnyCollection: boolean,
  ): void {
    const data = this.buildWishlistRemoveData(product, selectedArticle);
    const trackingCallback = () => trackRemoveFromWishlist(
      data.syan, data.colorId, data.articleId, data.tracking, isOnAnyCollection,
    );
    wishlistService.removeFromWishlist(data, trackingCallback);
  }

  protected executeWishlistUndo(product: Product, selectedArticle: Article | null): void {
    trackSnackbarUndoWishlistItemDelete(product);
    document.dispatchEvent(
      new CustomEvent(ADD_TO_WISHLIST_EVENT, {detail: this.buildWishlistRemoveData(product, selectedArticle)}),
    );
  }

  protected pushRemoveActionToSnackbar(
    imageCurrentSrc: string,
    isOnAnyCollection: boolean,
    undoAction?: () => void,
  ): void {
    const articleDeletedText = isOnAnyCollection
      ? this.messages['ents_wishlist_delete_all_title']
      : this.messages['ents_wishlist_delete_title'];
    pushToSnackbar(
      articleDeletedText!,
      imageCurrentSrc,
      undoAction ? this.messages['ents_wishlist_delete_buttontext'] : undefined,
      undoAction,
    );
  }
}
